import React, { useLayoutEffect, useCallback } from 'react';
import { useNavigate, useAsyncValue } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

// Components Styled
import {
  Container,
  BudgetCard,
  TitleTagContainer,
  Title,
  RowContainer,
  ColumnsContainer,
  Column,
  ColumnTitleContainer,
  SmallTitle,
  RowSubContainer,
  RowSubContainerLeft,
  RowSubContainerRight,
  RowContainerLast,
  ContainerButtons,
  CategoryIndicator,
} from './styles';

// Components
import { Typography } from '../../../../../components/display/Typography';
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';
import InputRoid from '../../../../../components/inputs/InputRoid';
import { Definition } from '../../../../../components/display/Definition';

// Types
import { BudgetItemSummaryData } from './types';

export default function BudgetItemSummary(): JSX.Element {
  const { t } = useTranslation(['budgets', 'buttons']);
  const loaderData = useAsyncValue() as BudgetItemSummaryData;
  const navigate = useNavigate();

  const budgetItemData = loaderData;

  useLayoutEffect(() => {
    if (!budgetItemData) navigate('..');
  }, [budgetItemData, navigate]);

  const handleBack = useCallback(() => {
    navigate('..', { relative: 'path' });
  }, [navigate]);

  return (
    <Container>
      {budgetItemData && (
        <BudgetCard>
          <TitleTagContainer>
            <Title size="L" variant="semiBold">
              {t('budgets:summary.title')}
            </Title>
          </TitleTagContainer>

          <RowContainer>
            <InputRoid
              label="ROID"
              roid={budgetItemData.action.roid.split('.')}
              disabled
              onChangeInputRoid={() => null}
            />
          </RowContainer>

          <RowContainer>
            <RowSubContainerLeft>
              <ColumnTitleContainer>
                <SmallTitle size="M" variant="semiBold">
                  {t('budgets:summary.budgetItemInfo')}
                </SmallTitle>
              </ColumnTitleContainer>
              <Column>
                <Definition
                  title={t('budgets:summary.budgetItemCode')}
                  value={budgetItemData.budgetItem.code.toString()}
                />
                <Definition
                  title={t('budgets:summary.description')}
                  value={budgetItemData.budgetItem.description}
                />
                <Definition
                  title={t('budgets:summary.unit')}
                  value={budgetItemData.budgetItem.unit}
                />
              </Column>
            </RowSubContainerLeft>

            <RowSubContainerRight>
              <ColumnTitleContainer>
                <SmallTitle size="M" variant="semiBold">
                  {t('budgets:summary.economicInfo')}
                </SmallTitle>
              </ColumnTitleContainer>
              <Column>
                <Definition
                  title={t('budgets:summary.quantity')}
                  value={`${budgetItemData.quantity} ${budgetItemData.budgetItem.unit}`}
                />
                <Definition
                  title={t('budgets:summary.unitPrice')}
                  value={`${budgetItemData.budgetItem.unitPrice.toFixed(2)} €`}
                />
                <Definition
                  title={t('budgets:summary.totalAmount')}
                  value={`${budgetItemData.totalAmount.toFixed(2)} €`}
                />
              </Column>
            </RowSubContainerRight>
          </RowContainer>

          <RowContainer>
            <RowSubContainer>
              <ColumnTitleContainer>
                <SmallTitle size="M" variant="semiBold">
                  {t('budgets:summary.budgetCategory')}
                </SmallTitle>
              </ColumnTitleContainer>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <CategoryIndicator
                  color={budgetItemData.budgetItem.budgetCategory.color}
                />
                <Typography size="M" variant="semiBold">
                  {budgetItemData.budgetItem.budgetCategory.code} -{' '}
                  {budgetItemData.budgetItem.budgetCategory.name}
                </Typography>
              </div>
            </RowSubContainer>
          </RowContainer>

          <RowContainerLast>
            <RowSubContainer>
              <ColumnTitleContainer>
                <SmallTitle size="M" variant="semiBold">
                  {t('budgets:summary.actionInfo')}
                </SmallTitle>
              </ColumnTitleContainer>
              <Column>
                <Definition
                  title={t('budgets:summary.actionDescription')}
                  value={budgetItemData.action.actionTitle}
                />
                <Definition
                  title={t('budgets:summary.registrationDate')}
                  value={format(
                    new Date(budgetItemData.action.registerInitDate),
                    'dd/MM/yyyy HH:mm'
                  )}
                />
                {budgetItemData.action.concessions.length > 0 && (
                  <Definition
                    title={t('budgets:summary.concession')}
                    value={budgetItemData.action.concessions[0].name}
                  />
                )}
                {budgetItemData.action.externalCompanies.length > 0 && (
                  <Definition
                    title={t('budgets:summary.externalCompanies')}
                    value={budgetItemData.action.externalCompanies.join(', ')}
                  />
                )}
              </Column>
            </RowSubContainer>
          </RowContainerLast>

          <ContainerButtons>
            <MainButton
              variant="secondary"
              text={t('buttons:back')}
              onClick={handleBack}
            />
          </ContainerButtons>
        </BudgetCard>
      )}
    </Container>
  );
}
